import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Language = 'de' | 'en' | 'fr' | 'it';

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

// Translation data
const translations = {
  de: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.folders': 'Ordner',
    'nav.budget': 'Budget',
    'nav.assets': 'Vermögen',
    'nav.login': 'Anmelden',
    'nav.logout': 'Abmelden',
    'nav.profile': 'Profil',
    'nav.settings': 'Einstellungen',

    // Settings page
    'settings.title': 'Einstellungen',
    'settings.subtitle': 'Verwalte dein Konto und deine Einstellungen',
    'settings.account.title': 'Konto',
    'settings.account.status': 'Kontostatus',
    'settings.account.loggedIn': 'Du bist angemeldet',
    'settings.account.active': 'Aktiv',
    'settings.account.logout': 'Abmelden',
    'settings.account.logoutDescription': 'Von Ihrem Konto abmelden',
    'settings.preferences.title': 'Einstellungen',
    'settings.preferences.language': 'Sprache',
    'settings.preferences.currentLanguage': 'Aktuell: {language}',
    'settings.budget.title': 'Budget-Konfiguration',
    'settings.budget.reAnswerQuestions': 'Onboarding-Fragen erneut beantworten',
    'settings.budget.reAnswerDescription': 'Setze deine Budget-Konfiguration zurück und beantworte die Onboarding-Fragen erneut',
    'settings.budget.resetting': 'Zurücksetzen...',
    'settings.budget.reAnswerButton': 'Fragen erneut beantworten',
    'settings.budget.note': 'Hinweis:',
    'settings.budget.noteText': 'Dies löscht deine aktuellen Fragebogendaten und leitet dich weiter, um die Onboarding-Fragen erneut zu beantworten. Deine gespeicherten Budgetdaten bleiben intakt.',
    'settings.loginRequired': 'Bitte melde dich an, um auf die Einstellungen zuzugreifen.',
    'settings.avatar.title': 'Avatar auswählen',
    'settings.avatar.current': 'Aktueller Avatar',
    'settings.avatar.male': 'Männlich',
    'settings.avatar.female': 'Weiblich',
    'settings.avatar.neutral': 'Neutral',

    // Dashboard
    'dashboard.title': 'Grüezi.',
    'dashboard.folders.title': 'Ordner verwalten',
    'dashboard.folders.description': 'Generiere Register für deine Dokumente',
    'dashboard.budget.title': 'Budget',
    'dashboard.budget.description': 'Verwalte deine Einnahmen und Ausgaben',
    'dashboard.income': 'Gesamteinnahmen',
    'dashboard.expenses': 'Gesamtausgaben',
    'dashboard.savings': 'Jahresersparnis',
    'dashboard.surplus': 'Überschuss',
    'dashboard.deficit': 'Defizit',
    'dashboard.perYear': 'pro Jahr',

    // Activity time
    'activity.justNow': 'gerade eben',
    'activity.minutesAgo': 'vor {count} Min',
    'activity.hoursAgo': 'vor {count} Std',
    'activity.daysAgo': 'vor {count} Tag{count, plural, one {} other {en}}',
    
    // Activity actions
    'activity.budgetUpdated': 'Budget aktualisiert',
    'activity.foldersUpdated': 'Ordner aktualisiert',
    'activity.registerGenerated': 'Register generiert',
    'dashboard.activities': 'Letzte Aktivitäten',
    'dashboard.activities.description': 'Ihre neuesten Aktionen',
    'dashboard.activities.refresh': 'Aktualisieren',
    'dashboard.activities.loading': 'Lade Aktivitäten...',
    'dashboard.activities.empty': 'Noch keine Aktivitäten',
    'dashboard.activities.empty.description': 'Beginne mit der Nutzung der Ordner oder Budget-Funktionen',
    'dashboard.quickStats': 'Schnellübersicht',
    'dashboard.monthlySavings': 'Monatliche Ersparnis',
    'dashboard.budgetStatus': 'Budget Status',
    'dashboard.positive': 'Positiv',

    // Folders App
    'folders.title': 'Ordner',
    'folders.subtitle': 'Hier findest du deine fünf Ordner',
    'folders.admin': 'ADMIN',
    'folders.admin.description': 'Für amtliche Dokumente',
    'folders.work': 'WORK',
    'folders.work.description': 'Für Dokumente zu Ausbildung und Anstellung',
    'folders.tax': 'TAX',
    'folders.tax.description': 'Für steuerliche Dokumente',
    'folders.contracts': 'CONTRACTS',
    'folders.contracts.description': 'Für Verträge mit Unternehmen',
    'folders.taxArchive': 'TAX ARCHIVE',
    'folders.taxArchive.description': 'Für archivierte Steuerdokumente',
    'folders.generatePDF': 'PDF generieren',
    'folders.items': 'Einträge',
    'folders.option': 'Option',
    'folders.description': 'Beschreibung',
    'folders.category': 'Kategorie',
    'folders.selectCategory': 'Kategorie wählen',
    'folders.empty': 'Leer',
    'folders.dragDropInstruction': 'Ziehe die Einträge per Drag & Drop um, um die Reihenfolge zu ändern',
    'folders.loginRequired': 'Anmeldung erforderlich',
    'folders.loginRequired.description': 'Du musst dich anmelden, um auf die Ordner-Funktionen zuzugreifen.',
    'folders.loginButton': 'Zur Anmeldung',
    'folders.standard': 'Standard',
    'folders.customInput': 'Benutzerdefinierte Eingabe',

    // Login
    'login.title': 'Anmelden',
    'login.email': 'E-Mail-Adresse',
    'login.password': 'Passwort',
    'login.login': 'Anmelden',
    'login.loggingIn': 'Anmeldung läuft...',
    'login.loginFailed': 'Anmeldung fehlgeschlagen',
    'login.fillAllFields': 'Bitte fülle alle Felder aus',
    'login.createAccount': 'Registrieren',
    'login.note': 'Falls du noch kein Konto hast, kannst du dich registrieren',
    'login.noteLabel': 'Hinweis:',
    'login.emailPlaceholder': 'ihre.email@beispiel.ch',
    'login.passwordPlaceholder': 'Ihr Passwort',

    // Signup
    'signup.title': 'Erstelle dein Konto',
    'signup.firstName': 'Vorname',
    'signup.lastName': 'Nachname',
    'signup.email': 'E-Mail *',
    'signup.emailPlaceholder': 'E-Mail',
    'signup.password': 'Passwort *',
    'signup.passwordPlaceholder': 'Passwort',
    'signup.confirmPassword': 'Passwort bestätigen',
    'signup.confirmPasswordPlaceholder': 'Passwort wiederholen',
    'signup.createAccount': 'Registrieren',
    'signup.creatingAccount': 'Konto wird erstellt...',
    'signup.backToQuestionnaire': 'Zurück zum Fragebogen',
    'signup.goToLoginNote': 'Du hast bereits ein Konto?',
    'signup.goToLoginLink': 'Anmelden',
    'signup.note': 'Hinweis:',
    'signup.noteDescription': 'Ihre Fragebogendaten werden automatisch mit Ihrem neuen Konto verknüpft',
    'signup.fillAllFields': 'Bitte fülle alle Felder aus',
    'signup.passwordsDoNotMatch': 'Passwörter stimmen nicht überein',
    'signup.passwordTooShort': 'Passwort muss mindestens 8 Zeichen lang sein',
    'signup.passwordMissingUppercase': 'Passwort muss mindestens einen Grossbuchstaben enthalten',
    'signup.passwordMissingNumber': 'Passwort muss mindestens eine Zahl enthalten',
    'signup.passwordMissingSymbol': 'Passwort muss mindestens ein Symbol enthalten',
    'signup.signupFailed': 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.',
    'signup.privacyNote': 'Bitte nutze eine anonyme E-Mail-Adresse, um dein Konto zu erstellen, damit wir deine Privatsphäre bestmöglich schützen können.',
    'signup.protonMail.proton': 'Proton',
    'signup.protonMail.mail': 'Mail',

    // Admin folder options
    'admin.residence_certificate': 'Wohnsitzbescheinigung',
    'admin.pension_fund': 'Pensionskasse',
    'admin.health_insurance': 'Krankenkasse',
    'admin.daycare': 'Kita',
    'admin.car': 'Auto',
    'admin.official_documents': 'amt. Dokumente',
    'admin.military_service': 'Wehrpflicht',
    'admin.power_of_attorney': 'Vollmacht',
    'admin.inheritance_contract': 'Erbvertrag',

    // Work folder options
    'work.old_contracts': 'Alte Arbeitsverträge',
    'work.references': 'Arbeitszeugnisse',
    'work.certificates': 'Zertifikate',
    'work.diplomas': 'Diplome',
    'work.employment_contract': 'Arbeitsvertrag',

    // Tax folder options
    'tax.wage_statement': 'Lohnausweis',
    'tax.public_transport': 'ÖV Abo',
    'tax.pillar3_confirmation': '3. Säule Bestätigung',
    'tax.health_insurance': 'Krankenkasse',
    'tax.bank': 'Bank',
    'tax.daycare_certificate': 'Kita Bescheinigung',
    'tax.health_costs': 'Gesundheitskosten',
    'tax.donations': 'Spenden',
    'tax.rental_deposit': 'Mietkautionskonto',
    'tax.serafe': 'Serafe',

    // Contracts folder options
    'contracts.household_liability_insurance': 'Hausrat- & Haftpflichtversicherung',
    'contracts.credit_card': 'Kreditkarte',
    'contracts.bank': 'Bank',
    'contracts.property_management': 'Verwaltungsunternehmen',
    'contracts.municipal_utilities': 'Städtische Werke',
    'contracts.cleaning_company': 'Haushaltsreinigung Unternehmen',
    'contracts.loyalty_card': 'Loyalitätskarte',
    'contracts.leasing': 'Leasing',
    'contracts.warranties_invoices': 'Garantien & Rechnungen',
    'contracts.loans': 'Darlehen',

    // Tax Archive folder options
    'taxarchive.tax_2026': 'Steuer 2026',
    'taxarchive.tax_2025': 'Steuer 2025',
    'taxarchive.tax_2024': 'Steuer 2024',
    'taxarchive.tax_2023': 'Steuer 2023',
    'taxarchive.tax_2022': 'Steuer 2022',
    'taxarchive.tax_2021': 'Steuer 2021',
    'taxarchive.tax_2020': 'Steuer 2020',
    'taxarchive.tax_2019': 'Steuer 2019',
    'taxarchive.tax_2018': 'Steuer 2018',
    'taxarchive.tax_2017': 'Steuer 2017',
    'taxarchive.tax_2016': 'Steuer 2016',
    'taxarchive.tax_2015': 'Steuer 2015',

    // Budget App
    'budget.title': 'Budget',
    'budget.subtitle': 'Erfasse und berechne deine Einnahmen und Ausgaben',
    'budget.income': 'Einnahmen',
    'budget.expenses': 'Ausgaben',
    'budget.monthly': 'Monatlich (CHF)',
    'budget.annual': 'Jährlich (CHF)',
    'budget.save': 'Speichern',
    'budget.saved': 'Gespeichert',
    'budget.export': 'Exportieren',
    'budget.reset': 'Zurücksetzen',
    'budget.unsavedChanges': 'Ungespeicherte Änderungen',
    'budget.standard': 'Standard',
    'budget.loading': 'Lade Budgetrechner...',
    'budget.loginRequired': 'Anmeldung erforderlich',
    'budget.loginRequired.description': 'Bitte melde dich an, um den Budgetrechner zu verwenden.',
    'budget.loginButton': 'Zur Anmeldung',
    'budget.totalIncome': 'Gesamteinnahmen',
    'budget.totalExpenses': 'Gesamtausgaben',
    'budget.annualSavings': 'Jahresersparnis',
    'budget.savings': 'Ersparnis',
    'budget.perYear': 'pro Jahr',

    // Common
    'common.loading': 'Laden...',
    'common.error': 'Fehler',
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.delete': 'Löschen',
    'common.edit': 'Bearbeiten',
    'common.close': 'Schliessen',
    'common.yes': 'Ja',
    'common.no': 'Nein',
    'common.ok': 'OK',
    'common.chf': 'CHF',
    'common.eur': 'EUR',

    // Questionnaire
    'questionnaire.back': 'Zurück',
    'questionnaire.skip': 'Überspringen',
    'questionnaire.next': 'Weiter',
    'questionnaire.saving': 'Speichern...',
    'questionnaire.saveError.noConnection': 'Keine Verbindung zum Server',
    'questionnaire.saveError.failed': 'Fehler beim Speichern der Daten',
    'questionnaire.familyStatus.title': 'Wie ist Ihre Familiensituation?',
    'questionnaire.familyStatus.subtitle': 'Dies beeinflusst viele Ausgabenkategorien',
    'questionnaire.familyStatus.single': '👤 Single',
    'questionnaire.familyStatus.singleDesc': 'Allein lebend',
    'questionnaire.familyStatus.couple': '👫 Paar',
    'questionnaire.familyStatus.coupleDesc': 'Ohne Kinder',
    'questionnaire.childrenCount.title': 'Kinder',
    'questionnaire.childrenCount.placeholder': 'Anzahl Kinder',
    'questionnaire.completion.title': 'Du bist startklar.',
    'questionnaire.completion.subtitle': 'Mit dem, was du eingegeben hast, können wir schon starten. Mit ein paar Details mehr wird\'s richtig smart.',
    'questionnaire.completion.goToDashboard': 'Zum Dashboard',
    'questionnaire.completion.startFineTuning': 'Feintuning starten',
    
    // Budget Categories
    'budget.categories.income': 'Einnahmen',
    'budget.categories.housing': 'Wohnen',
    'budget.categories.family': 'Familie',
    'budget.categories.household': 'Haushalt & Konsum',
    'budget.categories.mobility': 'Mobilität',
    'budget.categories.communication': 'Kommunikation',
    'budget.categories.taxes': 'Steuern',
    'budget.categories.health': 'Gesundheit',
    'budget.categories.insurance': 'Versicherungen',
    'budget.categories.leisure': 'Freizeit & Hobbies',
    'budget.categories.savings': 'Sparen',
    'budget.categories.miscellaneous': 'Verschiedenes',
    
    // Budget Items
    'budget.items.travel': 'Reisen',
    'budget.items.other': 'Sonstiges',
    'budget.items.net-income-1': 'Nettoeinkommen Person 1',
    'budget.items.net-income-2': 'Nettoeinkommen Person 2',
    'budget.items.additional-income': 'Nebeneinkünfte',
    'budget.items.alimony': 'Alimente',
    'budget.items.pension': 'Rente',
    'budget.items.other-income': 'Weitere Einnahmen',
    'budget.items.rent': 'Miete / Eigentum',
    'budget.items.utilities': 'Nebenkosten',
    'budget.items.maintenance': 'Instandhaltung',
    'budget.items.childcare': 'Kinderbetreuung',
    'budget.items.education': 'Bildung',
    'budget.items.family-other': 'Sonstiges',
    'budget.items.groceries': 'Lebensmittel',
    'budget.items.clothing': 'Kleidung',
    'budget.items.household-items': 'Haushaltsgegenstände',
    'budget.items.car': 'Auto',
    'budget.items.public-transport': 'Öffentliche Verkehrsmittel',
    'budget.items.fuel': 'Kraftstoff',
    'budget.items.phone': 'Telefon',
    'budget.items.internet': 'Internet',
    'budget.items.tv': 'TV/Streaming',
    'budget.items.income-tax': 'Einkommensteuer',
    'budget.items.property-tax': 'Grundsteuer',
    'budget.items.other-taxes': 'Sonstige Steuern',
    'budget.items.health-insurance': 'Krankenversicherung',
    'budget.items.medical-expenses': 'Medizinische Ausgaben',
    'budget.items.pharmacy': 'Apotheke',
    'budget.items.liability': 'Haftpflicht',
    'budget.items.household-insurance': 'Hausrat',
    'budget.items.life-insurance': 'Lebensversicherung',
    'budget.items.emergency-fund': 'Notgroschen',
    'budget.items.sports': 'Sport',
    'budget.items.hobbies': 'Hobbies',
    'budget.items.entertainment': 'Unterhaltung',
    'budget.items.retirement': 'Altersvorsorge',
    'budget.items.investments': 'Investitionen',
    'budget.items.gifts': 'Geschenke',
    'budget.items.donations': 'Spenden',
  },
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.folders': 'Folders',
    'nav.budget': 'Budget',
    'nav.assets': 'Assets',
    'nav.login': 'Login',
    'nav.logout': 'Logout',
    'nav.profile': 'Profile',
    'nav.settings': 'Settings',

    // Settings page
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your account and preferences',
    'settings.account.title': 'Account',
    'settings.account.status': 'Account Status',
    'settings.account.loggedIn': 'You are logged in',
    'settings.account.active': 'Active',
    'settings.account.logout': 'Logout',
    'settings.account.logoutDescription': 'Sign out of your account',
    'settings.preferences.title': 'Preferences',
    'settings.preferences.language': 'Language',
    'settings.preferences.currentLanguage': 'Current: {language}',
    'settings.budget.title': 'Budget Configuration',
    'settings.budget.reAnswerQuestions': 'Re-answer Onboarding Questions',
    'settings.budget.reAnswerDescription': 'Reset your budget configuration and answer the onboarding questions again',
    'settings.budget.resetting': 'Resetting...',
    'settings.budget.reAnswerButton': 'Re-answer Questions',
    'settings.budget.note': 'Note:',
    'settings.budget.noteText': 'This will clear your current questionnaire data and redirect you to answer the onboarding questions again. Your saved budget data will remain intact.',
    'settings.loginRequired': 'Log in to access settings.',
    'settings.avatar.title': 'Select Avatar',
    'settings.avatar.current': 'Current Avatar',
    'settings.avatar.male': 'Male',
    'settings.avatar.female': 'Female',
    'settings.avatar.neutral': 'Neutral',

    // Dashboard
    'dashboard.title': 'Hey there.',
    'dashboard.folders.title': 'Manage Folders',
    'dashboard.folders.description': 'Generate registers for your documents',
    'dashboard.budget.title': 'Budget',
    'dashboard.budget.description': 'Manage your income and expenses',
    'dashboard.income': 'Total Income',
    'dashboard.expenses': 'Total Expenses',
    'dashboard.savings': 'Annual Savings',
    'dashboard.surplus': 'Surplus',
    'dashboard.deficit': 'Deficit',
    'dashboard.perYear': 'per year',

    // Activity time
    'activity.justNow': 'just now',
    'activity.minutesAgo': '{count} min ago',
    'activity.hoursAgo': '{count} hr ago',
    'activity.daysAgo': '{count} day{count, plural, one {} other {s}} ago',
    
    // Activity actions
    'activity.budgetUpdated': 'Budget updated',
    'activity.foldersUpdated': 'Folders updated',
    'activity.registerGenerated': 'Register generated',
    'dashboard.activities': 'Recent Activities',
    'dashboard.activities.description': 'Your latest actions',
    'dashboard.activities.refresh': 'Refresh',
    'dashboard.activities.loading': 'Loading activities...',
    'dashboard.activities.empty': 'No activities yet',
    'dashboard.activities.empty.description': 'Start using the folder or budget functions',
    'dashboard.quickStats': 'Quick Overview',
    'dashboard.monthlySavings': 'Monthly Savings',
    'dashboard.budgetStatus': 'Budget Status',
    'dashboard.positive': 'Positive',

    // Folders App
    'folders.title': 'Folders',
    'folders.subtitle': 'Here you find your five folders',
    'folders.admin': 'ADMIN',
    'folders.admin.description': 'For official documents',
    'folders.work': 'WORK',
    'folders.work.description': 'For education and employment documents',
    'folders.tax': 'TAX',
    'folders.tax.description': 'For tax documents',
    'folders.contracts': 'CONTRACTS',
    'folders.contracts.description': 'For contracts with companies',
    'folders.taxArchive': 'TAX ARCHIVE',
    'folders.taxArchive.description': 'For archived tax documents',
    'folders.generatePDF': 'Generate PDF',
    'folders.items': 'Items',
    'folders.option': 'Option',
    'folders.description': 'Description',
    'folders.category': 'Category',
    'folders.selectCategory': 'Select category',
    'folders.empty': 'Empty',
    'folders.dragDropInstruction': 'Drag and drop entries to change the order',
    'folders.loginRequired': 'Login required',
    'folders.loginRequired.description': 'You must log in to access the folder functions.',
    'folders.loginButton': 'Go to login',
    'folders.standard': 'Standard',
    'folders.customInput': 'Custom Input',

    // Login
    'login.title': 'Login',
    'login.email': 'Email Address',
    'login.password': 'Password',
    'login.login': 'Login',
    'login.loggingIn': 'Logging in...',
    'login.loginFailed': 'Login failed',
    'login.fillAllFields': 'Please fill in all fields',
    'login.createAccount': 'Create Account',
    'login.note': 'If you don\'t have an account yet, you can register',
    'login.noteLabel': 'Note:',
    'login.emailPlaceholder': 'your.email@example.com',
    'login.passwordPlaceholder': 'Your password',

    // Signup
    'signup.title': 'Create Account',
    'signup.firstName': 'First Name',
    'signup.firstNamePlaceholder': 'Your first name',
    'signup.lastName': 'Last Name',
    'signup.lastNamePlaceholder': 'Your last name',
    'signup.email': 'Email Address',
    'signup.emailPlaceholder': 'your.email@example.com',
    'signup.password': 'Password',
    'signup.passwordPlaceholder': 'At least 8 characters',
    'signup.confirmPassword': 'Confirm Password',
    'signup.confirmPasswordPlaceholder': 'Repeat password',
    'signup.createAccount': 'Create Account',
    'signup.creatingAccount': 'Creating account...',
    'signup.backToQuestionnaire': 'Back to Questionnaire',
    'signup.note': 'Note:',
    'signup.noteDescription': 'Your questionnaire data will be automatically linked to your new account',
    'signup.fillAllFields': 'Please fill in all fields',
    'signup.passwordsDoNotMatch': 'Passwords do not match',
    'signup.passwordTooShort': 'Password must be at least 8 characters long',
    'signup.passwordMissingUppercase': 'Password must contain at least one uppercase letter',
    'signup.passwordMissingNumber': 'Password must contain at least one number',
    'signup.passwordMissingSymbol': 'Password must contain at least one symbol',
    'signup.signupFailed': 'Registration failed. Please try again.',
    'signup.privacyNote': 'Please use an anonymous email address to create your account so that we can best protect your privacy.',
    'signup.protonMail.proton': 'Proton',
    'signup.protonMail.mail': 'Mail',

    // Admin folder options
    'admin.residence_certificate': 'Residence Certificate',
    'admin.pension_fund': 'Pension Fund',
    'admin.health_insurance': 'Health Insurance',
    'admin.daycare': 'Daycare',
    'admin.car': 'Car',
    'admin.official_documents': 'Official Documents',
    'admin.military_service': 'Military Service',
    'admin.power_of_attorney': 'Power of Attorney',
    'admin.inheritance_contract': 'Inheritance Contract',

    // Work folder options
    'work.old_contracts': 'Old Employment Contracts',
    'work.references': 'Work References',
    'work.certificates': 'Certificates',
    'work.diplomas': 'Diplomas',
    'work.employment_contract': 'Employment Contract',

    // Tax folder options
    'tax.wage_statement': 'Wage Statement',
    'tax.public_transport': 'Public Transport',
    'tax.pillar3_confirmation': 'Pillar 3 Confirmation',
    'tax.health_insurance': 'Health Insurance',
    'tax.bank': 'Bank',
    'tax.daycare_certificate': 'Daycare Certificate',
    'tax.health_costs': 'Health Costs',
    'tax.donations': 'Donations',
    'tax.rental_deposit': 'Rental Deposit',
    'tax.serafe': 'Serafe',

    // Contracts folder options
    'contracts.household_liability_insurance': 'Household & Liability Insurance',
    'contracts.credit_card': 'Credit Card',
    'contracts.bank': 'Bank',
    'contracts.property_management': 'Property Management',
    'contracts.municipal_utilities': 'Municipal Utilities',
    'contracts.cleaning_company': 'Cleaning Company',
    'contracts.loyalty_card': 'Loyalty Card',
    'contracts.leasing': 'Leasing',
    'contracts.warranties_invoices': 'Warranties & Invoices',
    'contracts.loans': 'Loans',

    // Tax Archive folder options
    'taxarchive.tax_2026': 'Tax 2026',
    'taxarchive.tax_2025': 'Tax 2025',
    'taxarchive.tax_2024': 'Tax 2024',
    'taxarchive.tax_2023': 'Tax 2023',
    'taxarchive.tax_2022': 'Tax 2022',
    'taxarchive.tax_2021': 'Tax 2021',
    'taxarchive.tax_2020': 'Tax 2020',
    'taxarchive.tax_2019': 'Tax 2019',
    'taxarchive.tax_2018': 'Tax 2018',
    'taxarchive.tax_2017': 'Tax 2017',
    'taxarchive.tax_2016': 'Tax 2016',
    'taxarchive.tax_2015': 'Tax 2015',

    // Budget App
    'budget.title': 'Budget',
    'budget.subtitle': 'Record and calculate your income and expenses',
    'budget.income': 'Income',
    'budget.expenses': 'Expenses',
    'budget.monthly': 'Monthly (CHF)',
    'budget.annual': 'Annual (CHF)',
    'budget.save': 'Save',
    'budget.saved': 'Saved',
    'budget.export': 'Export',
    'budget.reset': 'Reset',
    'budget.unsavedChanges': 'Unsaved changes',
    'budget.standard': 'Standard',
    'budget.loading': 'Loading budget calculator...',
    'budget.loginRequired': 'Login required',
    'budget.loginRequired.description': 'Please log in to use the budget calculator.',
    'budget.loginButton': 'Go to Login',
    'budget.totalIncome': 'Total Income',
    'budget.totalExpenses': 'Total Expenses',
    'budget.annualSavings': 'Annual Savings',
    'budget.savings': 'Savings',
    'budget.perYear': 'per year',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.close': 'Close',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.chf': 'CHF',
    'common.eur': 'EUR',

    // Questionnaire
    'questionnaire.back': 'Back',
    'questionnaire.skip': 'Skip',
    'questionnaire.next': 'Next',
    'questionnaire.saving': 'Saving...',
    'questionnaire.saveError.noConnection': 'No connection to server',
    'questionnaire.saveError.failed': 'Failed to save data',
    'questionnaire.familyStatus.title': 'What is your family situation?',
    'questionnaire.familyStatus.subtitle': 'This affects many expense categories',
    'questionnaire.familyStatus.single': '👤 Single',
    'questionnaire.familyStatus.singleDesc': 'Living alone',
    'questionnaire.familyStatus.couple': '👫 Couple',
    'questionnaire.familyStatus.coupleDesc': 'Without children',
    'questionnaire.childrenCount.title': 'Children',
    'questionnaire.childrenCount.placeholder': 'Number of children',
    'questionnaire.completion.title': 'You\'re good to go.',
    'questionnaire.completion.subtitle': 'With what you have entered, we can already start. With a few more details, it will be really smart.',
    'questionnaire.completion.goToDashboard': 'To Dashboard',
    'questionnaire.completion.startFineTuning': 'Start fine-tuning',
    
    // Budget Categories
    'budget.categories.income': 'Income',
    'budget.categories.housing': 'Housing',
    'budget.categories.family': 'Family',
    'budget.categories.household': 'Household & Consumption',
    'budget.categories.mobility': 'Mobility',
    'budget.categories.communication': 'Communication',
    'budget.categories.taxes': 'Taxes',
    'budget.categories.health': 'Health',
    'budget.categories.insurance': 'Insurance',
    'budget.categories.leisure': 'Leisure & Hobbies',
    'budget.categories.savings': 'Savings',
    'budget.categories.miscellaneous': 'Miscellaneous',
    
    // Budget Items
    'budget.items.net-income-1': 'Net Income Person 1',
    'budget.items.net-income-2': 'Net Income Person 2',
    'budget.items.additional-income': 'Additional Income',
    'budget.items.alimony': 'Alimony',
    'budget.items.pension': 'Pension',
    'budget.items.other-income': 'Other Income',
    'budget.items.rent': 'Rent / Ownership',
    'budget.items.utilities': 'Utilities',
    'budget.items.maintenance': 'Maintenance',
    'budget.items.childcare': 'Childcare',
    'budget.items.education': 'Education',
    'budget.items.family-other': 'Other',
    'budget.items.groceries': 'Groceries',
    'budget.items.clothing': 'Clothing',
    'budget.items.household-items': 'Household Items',
    'budget.items.car': 'Car',
    'budget.items.public-transport': 'Public Transport',
    'budget.items.fuel': 'Fuel',
    'budget.items.phone': 'Phone',
    'budget.items.internet': 'Internet',
    'budget.items.tv': 'TV/Streaming',
    'budget.items.income-tax': 'Income Tax',
    'budget.items.property-tax': 'Property Tax',
    'budget.items.other-taxes': 'Other Taxes',
    'budget.items.health-insurance': 'Health Insurance',
    'budget.items.medical-expenses': 'Medical Expenses',
    'budget.items.pharmacy': 'Pharmacy',
    'budget.items.liability': 'Liability',
    'budget.items.household-insurance': 'Household Insurance',
    'budget.items.life-insurance': 'Life Insurance',
    'budget.items.sports': 'Sports',
    'budget.items.hobbies': 'Hobbies',
    'budget.items.entertainment': 'Entertainment',
    'budget.items.emergency-fund': 'Emergency Fund',
    'budget.items.retirement': 'Retirement',
    'budget.items.investments': 'Investments',
    'budget.items.gifts': 'Gifts',
    'budget.items.donations': 'Donations',
  },
  fr: {
    // Navigation
    'nav.dashboard': 'Tableau de bord',
    'nav.folders': 'Dossiers',
    'nav.budget': 'Budget',
    'nav.assets': 'Patrimoine',
    'nav.login': 'Connexion',
    'nav.logout': 'Déconnexion',
    'nav.profile': 'Profil',
    'nav.settings': 'Paramètres',

    // Settings page
    'settings.title': 'Paramètres',
    'settings.subtitle': 'Gérez votre compte et vos préférences',
    'settings.account.title': 'Compte',
    'settings.account.status': 'Statut du compte',
    'settings.account.loggedIn': 'Vous êtes connecté',
    'settings.account.active': 'Actif',
    'settings.account.logout': 'Déconnexion',
    'settings.account.logoutDescription': 'Se déconnecter de votre compte',
    'settings.preferences.title': 'Préférences',
    'settings.preferences.language': 'Langue',
    'settings.preferences.currentLanguage': 'Actuel : {language}',
    'settings.budget.title': 'Configuration du budget',
    'settings.budget.reAnswerQuestions': 'Répondre à nouveau aux questions d\'intégration',
    'settings.budget.reAnswerDescription': 'Réinitialisez votre configuration de budget et répondez à nouveau aux questions d\'intégration',
    'settings.budget.resetting': 'Réinitialisation...',
    'settings.budget.reAnswerButton': 'Répondre à nouveau aux questions',
    'settings.budget.note': 'Note :',
    'settings.budget.noteText': 'Cela effacera vos données de questionnaire actuelles et vous redirigera pour répondre à nouveau aux questions d\'intégration. Vos données de budget sauvegardées resteront intactes.',
    'settings.loginRequired': 'Connecte-toi pour accéder aux paramètres.',
    'settings.avatar.title': 'Sélectionner Avatar',
    'settings.avatar.current': 'Avatar actuel',
    'settings.avatar.male': 'Masculin',
    'settings.avatar.female': 'Féminin',
    'settings.avatar.neutral': 'Neutre',

    // Dashboard
    'dashboard.title': 'Salut.',
    'dashboard.folders.title': 'Gérer les dossiers',
    'dashboard.folders.description': 'Générer des registres pour vos documents',
    'dashboard.budget.title': 'Budget',
    'dashboard.budget.description': 'Gérer vos revenus et dépenses',
    'dashboard.income': 'Revenus totaux',
    'dashboard.expenses': 'Dépenses totales',
    'dashboard.savings': 'Épargne annuelle',
    'dashboard.surplus': 'Excédent',
    'dashboard.deficit': 'Déficit',
    'dashboard.perYear': 'par an',

    // Activity time
    'activity.justNow': 'à l\'instant',
    'activity.minutesAgo': 'il y a {count} min',
    'activity.hoursAgo': 'il y a {count} h',
    'activity.daysAgo': 'il y a {count} jour{count, plural, one {} other {s}}',
    
    // Activity actions
    'activity.budgetUpdated': 'Budget mis à jour',
    'activity.foldersUpdated': 'Dossiers mis à jour',
    'activity.registerGenerated': 'Registre généré',
    'dashboard.activities': 'Activités récentes',
    'dashboard.activities.description': 'Vos dernières actions',
    'dashboard.activities.refresh': 'Actualiser',
    'dashboard.activities.loading': 'Chargement des activités...',
    'dashboard.activities.empty': 'Aucune activité pour le moment',
    'dashboard.activities.empty.description': 'Commencez à utiliser les fonctions de dossiers ou de budget',
    'dashboard.quickStats': 'Aperçu rapide',
    'dashboard.monthlySavings': 'Épargne mensuelle',
    'dashboard.budgetStatus': 'Statut du budget',
    'dashboard.positive': 'Positif',

    // Folders App
    'folders.title': 'Dossiers',
    'folders.subtitle': 'Ici vous trouvez vos cinq dossiers',
    'folders.admin': 'ADMIN',
    'folders.admin.description': 'Pour les documents officiels',
    'folders.work': 'WORK',
    'folders.work.description': 'Pour les documents de formation et d\'emploi',
    'folders.tax': 'TAX',
    'folders.tax.description': 'Pour les documents fiscaux',
    'folders.contracts': 'CONTRACTS',
    'folders.contracts.description': 'Pour les contrats avec les entreprises',
    'folders.taxArchive': 'TAX ARCHIVE',
    'folders.taxArchive.description': 'Pour les documents fiscaux archivés',
    'folders.generatePDF': 'Générer PDF',
    'folders.items': 'Éléments',
    'folders.option': 'Option',
    'folders.description': 'Description',
    'folders.category': 'Catégorie',
    'folders.selectCategory': 'Sélectionner une catégorie',
    'folders.empty': 'Vide',
    'folders.dragDropInstruction': 'Glissez-déposez les entrées pour changer l\'ordre',
    'folders.loginRequired': 'Connexion requise',
    'folders.loginRequired.description': 'Vous devez vous connecter pour accéder aux fonctions de dossiers.',
    'folders.loginButton': 'Aller à la connexion',
    'folders.standard': 'Standard',
    'folders.customInput': 'Input personnalisé',

    // Login
    'login.title': 'Connexion',
    'login.email': 'Adresse e-mail',
    'login.password': 'Mot de passe',
    'login.login': 'Se connecter',
    'login.loggingIn': 'Connexion en cours...',
    'login.loginFailed': 'Échec de la connexion',
    'login.fillAllFields': 'Remplis tous les champs',
    'login.createAccount': 'Créer un compte',
    'login.note': 'Si vous n\'avez pas encore de compte, vous pouvez vous inscrire',
    'login.noteLabel': 'Note :',
    'login.emailPlaceholder': 'votre.email@exemple.fr',
    'login.passwordPlaceholder': 'Votre mot de passe',

    // Signup
    'signup.title': 'Créer un compte',
    'signup.firstName': 'Prénom',
    'signup.firstNamePlaceholder': 'Votre prénom',
    'signup.lastName': 'Nom',
    'signup.lastNamePlaceholder': 'Votre nom',
    'signup.email': 'Adresse e-mail',
    'signup.emailPlaceholder': 'votre.email@exemple.fr',
    'signup.password': 'Mot de passe',
    'signup.passwordPlaceholder': 'Au moins 8 caractères',
    'signup.confirmPassword': 'Confirmer le mot de passe',
    'signup.confirmPasswordPlaceholder': 'Répéter le mot de passe',
    'signup.createAccount': 'Créer un compte',
    'signup.creatingAccount': 'Création du compte...',
    'signup.backToQuestionnaire': 'Retour au questionnaire',
    'signup.note': 'Note :',
    'signup.noteDescription': 'Vos données de questionnaire seront automatiquement liées à votre nouveau compte',
    'signup.fillAllFields': 'Veuillez remplir tous les champs',
    'signup.passwordsDoNotMatch': 'Les mots de passe ne correspondent pas',
    'signup.passwordTooShort': 'Le mot de passe doit contenir au moins 8 caractères',
    'signup.passwordMissingUppercase': 'Le mot de passe doit contenir au moins une majuscule',
    'signup.passwordMissingNumber': 'Le mot de passe doit contenir au moins un chiffre',
    'signup.passwordMissingSymbol': 'Le mot de passe doit contenir au moins un symbole',
    'signup.signupFailed': 'Inscription échouée. Veuillez réessayer.',
    'signup.privacyNote': 'Veuillez utiliser une adresse e-mail anonyme pour créer votre compte afin que nous puissions mieux protéger votre vie privée.',
    'signup.protonMail.proton': 'Proton',
    'signup.protonMail.mail': 'Mail',

    // Admin folder options
    'admin.residence_certificate': 'Certificat de résidence',
    'admin.pension_fund': 'Caisse de pension',
    'admin.health_insurance': 'Assurance maladie',
    'admin.daycare': 'Crèche',
    'admin.car': 'Voiture',
    'admin.official_documents': 'Documents officiels',
    'admin.military_service': 'Service militaire',
    'admin.power_of_attorney': 'Procuration',
    'admin.inheritance_contract': 'Contrat de succession',

    // Work folder options
    'work.old_contracts': 'Anciens contrats de travail',
    'work.references': 'Références de travail',
    'work.certificates': 'Certificats',
    'work.diplomas': 'Diplômes',
    'work.employment_contract': 'Contrat de travail',

    // Tax folder options
    'tax.wage_statement': 'Relevé de salaire',
    'tax.public_transport': 'Transport public',
    'tax.pillar3_confirmation': 'Confirmation 3ème pilier',
    'tax.health_insurance': 'Assurance maladie',
    'tax.bank': 'Banque',
    'tax.daycare_certificate': 'Certificat de crèche',
    'tax.health_costs': 'Coûts de santé',
    'tax.donations': 'Dons',
    'tax.rental_deposit': 'Dépôt de garantie',
    'tax.serafe': 'Serafe',

    // Contracts folder options
    'contracts.household_liability_insurance': 'Assurance ménage et responsabilité',
    'contracts.credit_card': 'Carte de crédit',
    'contracts.bank': 'Banque',
    'contracts.property_management': 'Gestion immobilière',
    'contracts.municipal_utilities': 'Services municipaux',
    'contracts.cleaning_company': 'Entreprise de nettoyage',
    'contracts.loyalty_card': 'Carte de fidélité',
    'contracts.leasing': 'Leasing',
    'contracts.warranties_invoices': 'Garanties et factures',
    'contracts.loans': 'Prêts',

    // Tax Archive folder options
    'taxarchive.tax_2026': 'Impôt 2026',
    'taxarchive.tax_2025': 'Impôt 2025',
    'taxarchive.tax_2024': 'Impôt 2024',
    'taxarchive.tax_2023': 'Impôt 2023',
    'taxarchive.tax_2022': 'Impôt 2022',
    'taxarchive.tax_2021': 'Impôt 2021',
    'taxarchive.tax_2020': 'Impôt 2020',
    'taxarchive.tax_2019': 'Impôt 2019',
    'taxarchive.tax_2018': 'Impôt 2018',
    'taxarchive.tax_2017': 'Impôt 2017',
    'taxarchive.tax_2016': 'Impôt 2016',
    'taxarchive.tax_2015': 'Impôt 2015',

    // Budget App
    'budget.title': 'Budget',
    'budget.subtitle': 'Enregistrez et calculez vos revenus et dépenses',
    'budget.income': 'Revenus',
    'budget.expenses': 'Dépenses',
    'budget.monthly': 'Mensuel (CHF)',
    'budget.annual': 'Annuel (CHF)',
    'budget.save': 'Enregistrer',
    'budget.saved': 'Enregistré',
    'budget.export': 'Exporter',
    'budget.reset': 'Réinitialiser',
    'budget.unsavedChanges': 'Modifications non enregistrées',
    'budget.standard': 'Standard',
    'budget.loading': 'Chargement du calculateur de budget...',
    'budget.loginRequired': 'Connexion requise',
    'budget.loginRequired.description': 'Veuillez vous connecter pour utiliser le calculateur de budget.',
    'budget.loginButton': 'Aller à la connexion',
    'budget.totalIncome': 'Revenus totaux',
    'budget.totalExpenses': 'Dépenses totales',
    'budget.annualSavings': 'Épargne annuelle',
    'budget.savings': 'Épargne',
    'budget.perYear': 'par an',

    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.close': 'Fermer',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.ok': 'OK',
    'common.chf': 'CHF',
    'common.eur': 'EUR',

    // Questionnaire
    'questionnaire.back': 'Retour',
    'questionnaire.skip': 'Passer',
    'questionnaire.next': 'Suivant',
    'questionnaire.saving': 'Enregistrement...',
    'questionnaire.saveError.noConnection': 'Aucune connexion au serveur',
    'questionnaire.saveError.failed': 'Erreur lors de l\'enregistrement des données',
    'questionnaire.familyStatus.title': 'Quelle est votre situation familiale ?',
    'questionnaire.familyStatus.subtitle': 'Cela affecte de nombreuses catégories de dépenses',
    'questionnaire.familyStatus.single': '👤 Célibataire',
    'questionnaire.familyStatus.singleDesc': 'Vivant seul',
    'questionnaire.familyStatus.couple': '👫 Couple',
    'questionnaire.familyStatus.coupleDesc': 'Sans enfants',
    'questionnaire.childrenCount.title': 'Enfants',
    'questionnaire.childrenCount.placeholder': 'Nombre d\'enfants',
    'questionnaire.completion.title': 'Vous êtes prêt à commencer.',
    'questionnaire.completion.subtitle': 'Avec ce que vous avez saisi, nous pouvons déjà commencer. Avec quelques détails de plus, ce sera vraiment intelligent.',
    'questionnaire.completion.goToDashboard': 'Aller au tableau de bord',
    'questionnaire.completion.startFineTuning': 'Démarrer le réglage fin',
    
    
    // Budget Categories
    'budget.categories.income': 'Revenus',
    'budget.categories.housing': 'Logement',
    'budget.categories.family': 'Famille',
    'budget.categories.household': 'Ménage & Consommation',
    'budget.categories.mobility': 'Mobilité',
    'budget.categories.communication': 'Communication',
    'budget.categories.taxes': 'Impôts',
    'budget.categories.health': 'Santé',
    'budget.categories.insurance': 'Assurances',
    'budget.categories.leisure': 'Loisirs & Hobbies',
    'budget.categories.savings': 'Épargne',
    'budget.categories.miscellaneous': 'Divers',
    
    // Budget Items
    'budget.items.net-income-1': 'Revenu net Personne 1',
    'budget.items.net-income-2': 'Revenu net Personne 2',
    'budget.items.additional-income': 'Revenus supplémentaires',
    'budget.items.alimony': 'Pension alimentaire',
    'budget.items.pension': 'Retraite',
    'budget.items.other-income': 'Autres revenus',
    'budget.items.rent': 'Loyer / Propriété',
    'budget.items.utilities': 'Charges',
    'budget.items.maintenance': 'Entretien',
    'budget.items.childcare': 'Garde d\'enfants',
    'budget.items.education': 'Éducation',
    'budget.items.family-other': 'Autre',
    'budget.items.groceries': 'Épicerie',
    'budget.items.clothing': 'Vêtements',
    'budget.items.household-items': 'Articles ménagers',
    'budget.items.car': 'Voiture',
    'budget.items.public-transport': 'Transport public',
    'budget.items.fuel': 'Carburant',
    'budget.items.phone': 'Téléphone',
    'budget.items.internet': 'Internet',
    'budget.items.tv': 'TV/Streaming',
    'budget.items.income-tax': 'Impôt sur le revenu',
    'budget.items.property-tax': 'Impôt foncier',
    'budget.items.other-taxes': 'Autres impôts',
    'budget.items.health-insurance': 'Assurance maladie',
    'budget.items.medical-expenses': 'Dépenses médicales',
    'budget.items.pharmacy': 'Pharmacie',
    'budget.items.liability': 'Responsabilité civile',
    'budget.items.household-insurance': 'Assurance ménage',
    'budget.items.life-insurance': 'Assurance vie',
    'budget.items.sports': 'Sport',
    'budget.items.hobbies': 'Loisirs',
    'budget.items.entertainment': 'Divertissement',
    'budget.items.emergency-fund': 'Fonds d\'urgence',
    'budget.items.retirement': 'Retraite',
    'budget.items.investments': 'Investissements',
    'budget.items.gifts': 'Cadeaux',
    'budget.items.donations': 'Dons',
  },
  it: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.folders': 'Cartelle',
    'nav.budget': 'Budget',
    'nav.assets': 'Patrimonio',
    'nav.login': 'Accedi',
    'nav.logout': 'Esci',
    'nav.profile': 'Profilo',
    'nav.settings': 'Impostazioni',

    // Settings page
    'settings.title': 'Impostazioni',
    'settings.subtitle': 'Gestisci il tuo account e le tue preferenze',
    'settings.account.title': 'Account',
    'settings.account.status': 'Stato dell\'account',
    'settings.account.loggedIn': 'Sei connesso',
    'settings.account.active': 'Attivo',
    'settings.account.logout': 'Esci',
    'settings.account.logoutDescription': 'Disconnettiti dal tuo account',
    'settings.preferences.title': 'Preferenze',
    'settings.preferences.language': 'Lingua',
    'settings.preferences.currentLanguage': 'Attuale: {language}',
    'settings.budget.title': 'Configurazione del budget',
    'settings.budget.reAnswerQuestions': 'Rispondi di nuovo alle domande di onboarding',
    'settings.budget.reAnswerDescription': 'Reimposta la configurazione del budget e rispondi di nuovo alle domande di onboarding',
    'settings.budget.resetting': 'Reimpostazione...',
    'settings.budget.reAnswerButton': 'Rispondi di nuovo alle domande',
    'settings.budget.note': 'Nota:',
    'settings.budget.noteText': 'Questo cancellerà i dati del questionario attuali e ti reindirizzerà per rispondere di nuovo alle domande di onboarding. I tuoi dati di budget salvati rimarranno intatti.',
    'settings.loginRequired': 'Accedi per accedere alle impostazioni.',
    'settings.avatar.title': 'Seleziona Avatar',
    'settings.avatar.current': 'Avatar attuale',
    'settings.avatar.male': 'Maschile',
    'settings.avatar.female': 'Femminile',
    'settings.avatar.neutral': 'Neutro',

    // Dashboard
    'dashboard.title': 'Ciao.',
    'dashboard.folders.title': 'Gestisci cartelle',
    'dashboard.folders.description': 'Genera registri per i tuoi documenti',
    'dashboard.budget.title': 'Budget',
    'dashboard.budget.description': 'Gestisci le tue entrate e uscite',
    'dashboard.income': 'Entrate totali',
    'dashboard.expenses': 'Uscite totali',
    'dashboard.savings': 'Risparmio annuale',
    'dashboard.surplus': 'Eccedenza',
    'dashboard.deficit': 'Deficit',
    'dashboard.perYear': 'all\'anno',

    // Activity time
    'activity.justNow': 'proprio ora',
    'activity.minutesAgo': '{count} min fa',
    'activity.hoursAgo': '{count} ore fa',
    'activity.daysAgo': '{count} giorno{count, plural, one {} other {i}} fa',
    
    // Activity actions
    'activity.budgetUpdated': 'Budget aggiornato',
    'activity.foldersUpdated': 'Cartelle aggiornate',
    'activity.registerGenerated': 'Registro generato',
    'dashboard.activities': 'Attività recenti',
    'dashboard.activities.description': 'Le tue ultime azioni',
    'dashboard.activities.refresh': 'Aggiorna',
    'dashboard.activities.loading': 'Caricamento attività...',
    'dashboard.activities.empty': 'Nessuna attività ancora',
    'dashboard.activities.empty.description': 'Inizia a usare le funzioni di cartelle o budget',
    'dashboard.quickStats': 'Panoramica rapida',
    'dashboard.monthlySavings': 'Risparmio mensile',
    'dashboard.budgetStatus': 'Stato budget',
    'dashboard.positive': 'Positivo',

    // Folders App
    'folders.title': 'Cartelle',
    'folders.subtitle': 'Qui trovi le tue cinque cartelle',
    'folders.admin': 'ADMIN',
    'folders.admin.description': 'Per documenti ufficiali',
    'folders.work': 'WORK',
    'folders.work.description': 'Per documenti di formazione e lavoro',
    'folders.tax': 'TAX',
    'folders.tax.description': 'Per documenti fiscali',
    'folders.contracts': 'CONTRACTS',
    'folders.contracts.description': 'Per contratti con aziende',
    'folders.taxArchive': 'TAX ARCHIVE',
    'folders.taxArchive.description': 'Per documenti fiscali archiviati',
    'folders.generatePDF': 'Genera PDF',
    'folders.items': 'Elementi',
    'folders.option': 'Opzione',
    'folders.description': 'Descrizione',
    'folders.category': 'Categoria',
    'folders.selectCategory': 'Seleziona categoria',
    'folders.empty': 'Vuoto',
    'folders.dragDropInstruction': 'Trascina e rilascia le voci per cambiare l\'ordine',
    'folders.loginRequired': 'Accesso richiesto',
    'folders.loginRequired.description': 'Devi effettuare l\'accesso per utilizzare le funzioni delle cartelle.',
    'folders.loginButton': 'Vai al login',
    'folders.standard': 'Standard',
    'folders.customInput': 'Input personalizzato',

    // Login
    'login.title': 'Accesso',
    'login.email': 'Indirizzo email',
    'login.password': 'Password',
    'login.login': 'Accedi',
    'login.loggingIn': 'Accesso in corso...',
    'login.loginFailed': 'Accesso fallito',
    'login.fillAllFields': 'Compila tutti i campi',
    'login.createAccount': 'Crea account',
    'login.note': 'Se non hai ancora un account, puoi registrarti',
    'login.noteLabel': 'Nota:',
    'login.emailPlaceholder': 'tua.email@esempio.it',
    'login.passwordPlaceholder': 'La tua password',

    // Signup
    'signup.title': 'Crea account',
    'signup.firstName': 'Nome',
    'signup.firstNamePlaceholder': 'Il tuo nome',
    'signup.lastName': 'Cognome',
    'signup.lastNamePlaceholder': 'Il tuo cognome',
    'signup.email': 'Indirizzo email',
    'signup.emailPlaceholder': 'tua.email@esempio.it',
    'signup.password': 'Password',
    'signup.passwordPlaceholder': 'Almeno 8 caratteri',
    'signup.confirmPassword': 'Conferma password',
    'signup.confirmPasswordPlaceholder': 'Ripeti password',
    'signup.createAccount': 'Crea account',
    'signup.creatingAccount': 'Creazione account...',
    'signup.backToQuestionnaire': 'Torna al questionario',
    'signup.note': 'Nota:',
    'signup.noteDescription': 'I tuoi dati del questionario saranno automaticamente collegati al tuo nuovo account',
    'signup.fillAllFields': 'Compila tutti i campi',
    'signup.passwordsDoNotMatch': 'Le password non corrispondono',
    'signup.passwordTooShort': 'La password deve essere di almeno 8 caratteri',
    'signup.passwordMissingUppercase': 'La password deve contenere almeno una lettera maiuscola',
    'signup.passwordMissingNumber': 'La password deve contenere almeno un numero',
    'signup.passwordMissingSymbol': 'La password deve contenere almeno un simbolo',
    'signup.signupFailed': 'Registrazione fallita. Riprova.',
    'signup.privacyNote': 'Utilizza un indirizzo email anonimo per creare il tuo account in modo che possiamo proteggere al meglio la tua privacy.',
    'signup.protonMail.proton': 'Proton',
    'signup.protonMail.mail': 'Mail',

    // Admin folder options
    'admin.residence_certificate': 'Certificato di residenza',
    'admin.pension_fund': 'Cassa pensione',
    'admin.health_insurance': 'Assicurazione sanitaria',
    'admin.daycare': 'Asilo nido',
    'admin.car': 'Auto',
    'admin.official_documents': 'Documenti ufficiali',
    'admin.military_service': 'Servizio militare',
    'admin.power_of_attorney': 'Procura',
    'admin.inheritance_contract': 'Contratto di successione',

    // Work folder options
    'work.old_contracts': 'Vecchi contratti di lavoro',
    'work.references': 'Referenze di lavoro',
    'work.certificates': 'Certificati',
    'work.diplomas': 'Diplomi',
    'work.employment_contract': 'Contratto di lavoro',

    // Tax folder options
    'tax.wage_statement': 'Certificato di stipendio',
    'tax.public_transport': 'Trasporto pubblico',
    'tax.pillar3_confirmation': 'Conferma 3° pilastro',
    'tax.health_insurance': 'Assicurazione sanitaria',
    'tax.bank': 'Banca',
    'tax.daycare_certificate': 'Certificato asilo nido',
    'tax.health_costs': 'Costi sanitari',
    'tax.donations': 'Donazioni',
    'tax.rental_deposit': 'Deposito cauzionale',
    'tax.serafe': 'Serafe',

    // Contracts folder options
    'contracts.household_liability_insurance': 'Assicurazione casa e responsabilità',
    'contracts.credit_card': 'Carta di credito',
    'contracts.bank': 'Banca',
    'contracts.property_management': 'Gestione immobiliare',
    'contracts.municipal_utilities': 'Servizi municipali',
    'contracts.cleaning_company': 'Azienda di pulizie',
    'contracts.loyalty_card': 'Carta fedeltà',
    'contracts.leasing': 'Leasing',
    'contracts.warranties_invoices': 'Garanzie e fatture',
    'contracts.loans': 'Prestiti',

    // Tax Archive folder options
    'taxarchive.tax_2026': 'Tasse 2026',
    'taxarchive.tax_2025': 'Tasse 2025',
    'taxarchive.tax_2024': 'Tasse 2024',
    'taxarchive.tax_2023': 'Tasse 2023',
    'taxarchive.tax_2022': 'Tasse 2022',
    'taxarchive.tax_2021': 'Tasse 2021',
    'taxarchive.tax_2020': 'Tasse 2020',
    'taxarchive.tax_2019': 'Tasse 2019',
    'taxarchive.tax_2018': 'Tasse 2018',
    'taxarchive.tax_2017': 'Tasse 2017',
    'taxarchive.tax_2016': 'Tasse 2016',
    'taxarchive.tax_2015': 'Tasse 2015',

    // Budget App
    'budget.title': 'Budget',
    'budget.subtitle': 'Registra e calcola le tue entrate e uscite',
    'budget.income': 'Entrate',
    'budget.expenses': 'Uscite',
    'budget.monthly': 'Mensile (CHF)',
    'budget.annual': 'Annuale (CHF)',
    'budget.save': 'Salva',
    'budget.saved': 'Salvato',
    'budget.export': 'Esporta',
    'budget.reset': 'Ripristina',
    'budget.unsavedChanges': 'Modifiche non salvate',
    'budget.standard': 'Standard',
    'budget.loading': 'Caricamento calcolatore budget...',
    'budget.loginRequired': 'Accesso richiesto',
    'budget.loginRequired.description': 'Effettua l\'accesso per utilizzare il calcolatore budget.',
    'budget.loginButton': 'Vai al login',
    'budget.totalIncome': 'Entrate totali',
    'budget.totalExpenses': 'Uscite totali',
    'budget.annualSavings': 'Risparmio annuale',
    'budget.savings': 'Risparmio',
    'budget.perYear': 'all\'anno',

    // Common
    'common.loading': 'Caricamento...',
    'common.error': 'Errore',
    'common.save': 'Salva',
    'common.cancel': 'Annulla',
    'common.delete': 'Elimina',
    'common.edit': 'Modifica',
    'common.close': 'Chiudi',
    'common.yes': 'Sì',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.chf': 'CHF',
    'common.eur': 'EUR',

    // Questionnaire
    'questionnaire.back': 'Indietro',
    'questionnaire.skip': 'Salta',
    'questionnaire.next': 'Avanti',
    'questionnaire.saving': 'Salvataggio...',
    'questionnaire.saveError.noConnection': 'Nessuna connessione al server',
    'questionnaire.saveError.failed': 'Errore durante il salvataggio dei dati',
    'questionnaire.familyStatus.title': 'Qual è la tua situazione familiare?',
    'questionnaire.familyStatus.subtitle': 'Questo influenza molte categorie di spese',
    'questionnaire.familyStatus.single': '👤 Single',
    'questionnaire.familyStatus.singleDesc': 'Vivere da solo',
    'questionnaire.familyStatus.couple': '👫 Coppia',
    'questionnaire.familyStatus.coupleDesc': 'Senza figli',
    'questionnaire.childrenCount.title': 'Bambini',
    'questionnaire.childrenCount.placeholder': 'Numero di bambini',
    'questionnaire.completion.title': 'Sei pronto.',
    'questionnaire.completion.subtitle': 'Con quello che hai inserito, possiamo già iniziare. Con qualche dettaglio in più sarà davvero intelligente.',
    'questionnaire.completion.goToDashboard': 'Vai alla dashboard',
    'questionnaire.completion.startFineTuning': 'Inizia la messa a punto',
    
    
    // Budget Categories
    'budget.categories.income': 'Entrate',
    'budget.categories.housing': 'Abitazione',
    'budget.categories.family': 'Famiglia',
    'budget.categories.household': 'Casa & Consumo',
    'budget.categories.mobility': 'Mobilità',
    'budget.categories.communication': 'Comunicazione',
    'budget.categories.taxes': 'Tasse',
    'budget.categories.health': 'Salute',
    'budget.categories.insurance': 'Assicurazioni',
    'budget.categories.leisure': 'Tempo libero & Hobby',
    'budget.categories.savings': 'Risparmio',
    'budget.categories.miscellaneous': 'Varie',
    
    // Budget Items
    'budget.items.net-income-1': 'Reddito netto Persona 1',
    'budget.items.net-income-2': 'Reddito netto Persona 2',
    'budget.items.additional-income': 'Redditi aggiuntivi',
    'budget.items.alimony': 'Alimenti',
    'budget.items.pension': 'Pensione',
    'budget.items.other-income': 'Altri redditi',
    'budget.items.rent': 'Affitto / Proprietà',
    'budget.items.utilities': 'Spese accessorie',
    'budget.items.maintenance': 'Manutenzione',
    'budget.items.childcare': 'Cura dei bambini',
    'budget.items.education': 'Istruzione',
    'budget.items.family-other': 'Altro',
    'budget.items.groceries': 'Alimentari',
    'budget.items.clothing': 'Abbigliamento',
    'budget.items.household-items': 'Articoli per la casa',
    'budget.items.car': 'Auto',
    'budget.items.public-transport': 'Trasporti pubblici',
    'budget.items.fuel': 'Carburante',
    'budget.items.phone': 'Telefono',
    'budget.items.internet': 'Internet',
    'budget.items.tv': 'TV/Streaming',
    'budget.items.income-tax': 'Imposta sul reddito',
    'budget.items.property-tax': 'Imposta fondiaria',
    'budget.items.other-taxes': 'Altre tasse',
    'budget.items.health-insurance': 'Assicurazione sanitaria',
    'budget.items.medical-expenses': 'Spese mediche',
    'budget.items.pharmacy': 'Farmacia',
    'budget.items.liability': 'Responsabilità civile',
    'budget.items.household-insurance': 'Assicurazione domestica',
    'budget.items.life-insurance': 'Assicurazione sulla vita',
    'budget.items.sports': 'Sport',
    'budget.items.hobbies': 'Hobby',
    'budget.items.entertainment': 'Intrattenimento',
    'budget.items.emergency-fund': 'Fondo di emergenza',
    'budget.items.retirement': 'Pensione',
    'budget.items.investments': 'Investimenti',
    'budget.items.gifts': 'Regali',
    'budget.items.donations': 'Donazioni',
    'budget.items.travel': 'Viaggi',
    'budget.items.other': 'Altro',
  }
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('de');

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && ['de', 'en', 'fr', 'it'].includes(savedLanguage)) {
      setLanguage(savedLanguage);
    }
  }, []);

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = useCallback((key: string): string => {
    return (translations[language] as any)[key] || key;
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
